import { Text } from "@carbon6/c6-frontend-design";
import type { ReactNode } from "react";
import type { DatasetDetails } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import type { LoadState } from "@/components/dataset/loadState";
import { episodeQualityTagText } from "@/components/label";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import { formatNumber } from "@/utils";
import styles from "./DatasetReviewPanel.module.css";

/**
 * 에피소드 품질 롤업 카드. 에피소드 판정의 집계를 리뷰 판단의 근거로 보여준다.
 *
 * 이 카드는 데이터셋 라벨이 아니라 에피소드마다 매긴 태그의 비율이다. 칩 모양으로 두면 "이
 * 데이터셋에 이 라벨이 붙었다" 로 읽힌다. 그래서 비율 막대를 앞에 두어 집계임을 보여준다.
 *
 * Owns: 롤업 비율·개수의 표시.
 * Does not own: 에피소드 판정 편집(에피소드/파일 탭).
 */
export function EpisodeQualityCard({ state }: { state: LoadState<DatasetDetails> }): ReactNode {
  const rollup = state.status === "success" ? state.data.episode_quality_rollup : null;

  return (
    <section className={styles.card} aria-label="에피소드 품질">
      <div className={styles.cardHead}>
        <Text variant={hatcheryTextModes["caption-bold"]}>에피소드 품질</Text>
      </div>
      {state.status === "error" ? (
        <EmptyState size="sm" tone="error">
          에피소드 품질 집계를 불러오지 못했습니다.
        </EmptyState>
      ) : rollup === null ? (
        <LoadingState size="sm">에피소드 품질 집계를 불러오고 있습니다.</LoadingState>
      ) : rollup.length === 0 ? (
        <EmptyState size="sm">품질 태그가 매겨진 에피소드가 없습니다.</EmptyState>
      ) : (
        <div className={styles.rollupList}>
          {rollup.map((entry) => (
            <div key={entry.label} className={styles.rollupRow}>
              <span className={styles.rollupLabel}>{episodeQualityTagText(entry.label)}</span>
              <div className={styles.rollupTrack} aria-hidden="true">
                <div
                  className={styles.rollupFill}
                  style={{ width: `${Math.min(100, Math.round(entry.ratio * 100))}%` }}
                />
              </div>
              <span className={styles.rollupValue}>
                {formatPercent(entry.ratio)}
                <span className={styles.muted}> · {formatNumber(entry.episode_count)}개</span>
              </span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// 0 이 아닌데 반올림으로 0% 가 되면 "없음"으로 읽힌다.
function formatPercent(ratio: number): string {
  const percent = ratio * 100;
  if (percent > 0 && percent < 1) {
    return "<1%";
  }
  return `${Math.round(percent)}%`;
}
